import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { publicApi } from '../../services/portalApi';

/**
 * Where PayFast returns the buyer after a successful payment.
 *
 * The payment being done does NOT mean the site exists yet: provisioning is
 * driven by PayFast's server-to-server ITN, which frequently lands after the
 * buyer is already back here. So this polls until the pending registration
 * flips to 'provisioned' rather than assuming success — telling someone their
 * site is ready before it is would be the worse failure.
 */
const POLL_INTERVAL_MS = 2_000;
const POLL_TIMEOUT_MS = 90_000;

export default function RegisterSuccess() {
  const [status, setStatus] = useState<string>('checking');
  const [siteName, setSiteName] = useState<string>('');
  const [timedOut, setTimedOut] = useState(false);

  useEffect(() => {
    const token = sessionStorage.getItem('patrolit-claim-token');
    if (!token) {
      setStatus('unknown');
      return;
    }

    let cancelled = false;
    const startedAt = Date.now();

    async function poll() {
      if (cancelled) return;
      try {
        const r = await publicApi.registerStatus(token!);
        if (cancelled) return;
        setSiteName(r.site_name);
        setStatus(r.status);
        if (r.status === 'provisioned') {
          sessionStorage.removeItem('patrolit-claim-token');
          return;
        }
        if (r.status === 'failed') return;
      } catch {
        /* keep polling — a transient error is not a verdict */
      }
      if (Date.now() - startedAt > POLL_TIMEOUT_MS) {
        setTimedOut(true);
        return;
      }
      setTimeout(poll, POLL_INTERVAL_MS);
    }

    void poll();
    return () => { cancelled = true; };
  }, []);

  const done = status === 'provisioned';
  const failed = status === 'failed';

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
      <div className="bg-white rounded-2xl shadow-lg w-full max-w-md p-8 text-center">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">
          {done ? 'Your site is ready' : failed ? 'Something went wrong' : 'Payment received'}
        </h1>

        {done && (
          <>
            <p className="text-sm text-gray-600 mb-6">
              {siteName ? `${siteName} is set up.` : 'Your site is set up.'} Sign in with the
              email and password you chose.
            </p>
            <Link
              to="/login"
              className="inline-block px-6 py-2.5 rounded-lg bg-brand-600 text-white text-sm font-semibold hover:bg-brand-700 transition-colors"
            >
              Sign in
            </Link>
          </>
        )}

        {!done && !failed && !timedOut && (
          <>
            <p className="text-sm text-gray-600">
              Thanks — we're setting up{siteName ? ` ${siteName}` : ' your site'} now. This
              usually takes a few seconds.
            </p>
            <div className="mt-6 flex justify-center">
              <div className="h-2 w-2 rounded-full bg-brand-600 animate-pulse" />
            </div>
          </>
        )}

        {timedOut && !done && (
          <p className="text-sm text-gray-600">
            Your payment went through, but setup is taking longer than expected. We'll email you
            as soon as your site is ready — no need to pay again.
          </p>
        )}

        {failed && (
          <p className="text-sm text-gray-600">
            We couldn't finish setting up your site. Please contact us and we'll sort it out —
            do not pay again.
          </p>
        )}
      </div>
    </div>
  );
}
